import styled from "styled-components"

import { colors, Text } from "@minuthq/meatball-ui-react"

import { ParadiseTitle } from "src/components/Paradise/sharedStyles"
import { breakpoint } from "src/constants/breakpoints"
import { Breadcrumbs, IBreadcrumb } from "src/ui/Breadcrumbs/Breadcrumbs"
import { TextButton } from "src/ui/Button/TextButton"
import { CopyText } from "src/ui/CopyText/CopyText"
import { MBadge } from "src/ui/MBadge/MBadge"
import { spacing } from "src/ui/spacing"
import { TabPanel } from "src/ui/Tabs/TabPanel"
import { Tabs } from "src/ui/Tabs/Tabs"

export type TParadiseDetailsLayoutTab = {
  id: string
  label: string
  component: React.ReactNode
}

export function ParadiseDetailsLayout({
  meta,
  actions,
  left,
  right,
  banner,
  breadcrumbs,
  breadcrumbCopyValue,
  tabs,
}: {
  meta: React.ReactNode
  actions?: React.ReactNode
  left: React.ReactNode
  right: React.ReactNode
  banner?: React.ReactNode
  breadcrumbs: IBreadcrumb[]
  breadcrumbCopyValue?: string
  tabs?: TParadiseDetailsLayoutTab[]
}) {
  const generalView = (
    <BottomSection>
      <LeftSection>
        <DetailsInnerWrapper>{left}</DetailsInnerWrapper>
      </LeftSection>
      <RightSection>
        <DetailsInnerWrapper>{right}</DetailsInnerWrapper>
      </RightSection>
    </BottomSection>
  )

  // To avoid breaking changes (since left and right are currently required)
  if (!!tabs) {
    tabs = [
      {
        id: "general",
        label: "General",
        component: generalView,
      },
      ...tabs,
    ]
  }

  return (
    <DetailsWrapper>
      <TopSection $hasTabs={!!tabs}>
        {banner && <BannerWrapper>{banner}</BannerWrapper>}

        <TopMetaWrapper>
          <MetaWrapper>
            <BreadcrumbWrapper>
              <Breadcrumbs items={breadcrumbs} />
              {breadcrumbCopyValue && (
                <CopyText value={breadcrumbCopyValue} copiedText="Copied" />
              )}
            </BreadcrumbWrapper>
            {meta}
          </MetaWrapper>
          {actions}
        </TopMetaWrapper>
      </TopSection>

      {!!tabs ? (
        <TabWrapper
          labels={tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
          }))}
        >
          {tabs.map((tab) => (
            <TabPanel key={tab.id}>{tab.component}</TabPanel>
          ))}
        </TabWrapper>
      ) : (
        generalView
      )}
    </DetailsWrapper>
  )
}

export function ParadiseDetailsSection({
  title,
  totalCount,
  description,
  buttonLabel,
  onButtonClick,
  children,
}: {
  title: React.ReactNode
  totalCount?: number
  description?: React.ReactNode
  buttonLabel?: string
  onButtonClick?: () => void
  children?: React.ReactNode
}) {
  return (
    <SectionWrapper>
      <div>
        <SectionHeader>
          <ParadiseTitle>
            {title}
            {totalCount !== undefined && (
              <MBadge color="info" size="x-small">
                Total:{" "}
                <Text variant="subtitleS" as="span">
                  {totalCount}
                </Text>
              </MBadge>
            )}
          </ParadiseTitle>
          {buttonLabel && (
            <TextButton onClick={onButtonClick}>{buttonLabel}</TextButton>
          )}
        </SectionHeader>
        {description && (
          <Text variant="bodyS" color="secondary">
            {description}
          </Text>
        )}
      </div>
      {children}
    </SectionWrapper>
  )
}

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`
const SectionWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
const DetailsWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const MetaWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`

const TopSection = styled.div<{ $hasTabs?: boolean }>`
  padding-top: ${spacing.S};
  padding-bottom: ${(props) => (props.$hasTabs ? spacing.XS : spacing.S)};
  padding-inline: ${spacing.L};

  background-color: ${colors.neutral};
  border-bottom: ${(props) =>
    props.$hasTabs ? "none" : "1px solid " + colors.divider};
`

const TabWrapper = styled(Tabs)`
  border-bottom: 1px solid ${colors.divider};
  background-color: ${colors.neutral};
  padding-left: ${spacing.L};
`

const BannerWrapper = styled.div`
  margin-bottom: ${spacing.L};
`

const BreadcrumbWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const TopMetaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${spacing.M};

  @media (${breakpoint.normalUp}) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
  }
`

const BottomSection = styled.div`
  flex: 1;
  @media (${breakpoint.normalUp}) {
    display: flex;
  }
`

const LeftSection = styled.div`
  padding-inline: ${spacing.L};
  padding-block: ${spacing.M};

  @media (${breakpoint.normalUp}) {
    width: 30%;
    border-inline-end: 1px solid ${colors.divider};
    margin-block-end: 0;
    overflow-y: auto;
    padding-bottom: ${spacing.M};
  }
`

const RightSection = styled.div`
  padding-inline: ${spacing.L};
  padding-block: ${spacing.M};
  @media (${breakpoint.normalUp}) {
    width: 70%;
    overflow-y: auto;
  }
`

const DetailsInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
`
